import { Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { type FC, useMemo } from 'react';

import s from './styles.module.scss';
import MedicationItem from '../medication-item';
import MedicationItemFooter from './footer';

import type { IMedication } from 'infrastructure/interfaces';

interface MedicationReconciliationItemProps {
  item: IMedication;

  disableActions: boolean;
  loading: boolean;
  remediatingActionType?: 'accept' | 'ignore' | null;
  modifyItemGuid?: string;

  onAccept: (item: IMedication) => void;
  onIgnore: (guid: string) => void;

  status?: 'removed' | 'updated' | 'added' | 'unrecognized';
  message?: string;
}

const MedicationReconciliationItem: FC<MedicationReconciliationItemProps> = ({
  item,
  disableActions,
  loading,
  remediatingActionType,
  modifyItemGuid,
  onAccept,
  onIgnore,
  status = 'removed',
  message,
}) => {
  const { t } = useTranslation();

  const DEFAULT_MESSAGE = useMemo(
    () => ({
      removed: t('labels.medReconRemovedMessage'),
      updated: t('labels.medReconUpdatedMessage'),
      added: t('labels.medReconAddedMessage'),
      unrecognized: t('labels.medReconAddedMessage'),
    }),
    [t],
  );

  const loadingAccept = loading && remediatingActionType === 'accept';
  const loadingIgnore = loading && remediatingActionType === 'ignore';
  const disableAccept = disableActions || loadingIgnore;
  const disableIgnore = disableActions || loadingAccept;

  return (
    <div className={s['item-wrapper']}>
      <MedicationItem
        item={item}
        disableActions={disableActions}
        loading={loading}
        modifyItemGuid={modifyItemGuid}
        showActions={false}
        showNote={false}
        flat
      />
      <div className={s['item-divider']}>
        <Divider style={{ margin: 0 }} />
      </div>
      <MedicationItemFooter
        status={status}
        message={
          message ??
          (status === 'updated' && item.note
            ? item.note
            : DEFAULT_MESSAGE[status])
        }
        onAccept={() => onAccept(item)}
        onIgnore={() => onIgnore(item.guid)}
        loadingAccept={loadingAccept}
        loadingIgnore={loadingIgnore}
        disableAccept={disableAccept}
        disableIgnore={disableIgnore}
      />
    </div>
  );
};

export default MedicationReconciliationItem;
